import React, { useEffect, useState } from 'react'
import AppShell from '../../../components/layout/AppShell'
import { Select, Table, Empty, Alert } from 'antd';
import { useNavigate } from 'react-router-dom'
import { DEFAULT_PAGE_SIZE_LIMIT, DEFAULT_PAGE_SIZE_OPTIONS, OTC_STATUS_OPTIONS, DATE_FILTER_OPTIONS } from '../../../utils/constants';
import { getOtcRequests } from '../../../services/api'
import { ArrowRightOutlined } from "@ant-design/icons"
import Badge from '../../../components/ui/Badge';
import DateFilter from '../../../components/ui/DateFilter';
import { formatAmount, getMaxDecimalsForCurrency, getDateFilterRange } from '../../../utils';

// Kill-switch for this page's <DateFilter />.
const DISABLE_DATE_FILTER = true;

const fmtDate = (dateStr) => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

const OtcList = () => {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState('');
    const [otcData, setOtcData] = useState({
        otcRequests: [],
        meta: {
            total: 0,
            page: 0,
            limit: 0,
            totalPages: 0,
        },
    });
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_LIMIT)
    const [loadingTxns, setLoadingTxns] = useState(false);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState(() =>
        getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
    );

    useEffect(() => {
        setLoadingTxns(true);
        setError('');
        getOtcRequests({
            page,
            limit: pageSize,
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(!DISABLE_DATE_FILTER && dateRange.startDate ? { from: dateRange.startDate } : {}),
            ...(!DISABLE_DATE_FILTER && dateRange.endDate ? { to: dateRange.endDate } : {}),
        })
            .then((res) => {
                const payload = res?.data || {};
                setOtcData({
                    otcRequests: payload.otcRequests || [],
                    meta: payload.meta || {},
                });
                setTotal(payload.meta?.total || 0);
            })
            .catch(() => {
                setOtcData({ otcRequests: [], meta: {} });
                setTotal(0);
                setError('Failed to load OTC requests. Please try again.');
            })
            .finally(() => setLoadingTxns(false));
    }, [page, pageSize, statusFilter, dateRange])

    const changeDateFilter = (_filter, range) => {
        setDateRange((prev) =>
            prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
        );
        setPage(1);
    };

    const otcColumnStructure = [
        {
            title: "SN",
            key: "sn",
            width: 60,
            render: (_, __, index) => (page - 1) * pageSize + index + 1,
        },
        {
            title: 'Pair',
            key: 'pair',
            width: 130,
            fixed: 'left',
            render: (_, tx) => {
                const from = tx.fromCurrency
                const to = tx.toCurrency
                if (!from || !to) return <span className="text-gray-400">—</span>
                return (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-md whitespace-nowrap">
                        {from} <ArrowRightOutlined /> {to}
                    </span>
                )
            },
        },
        {
            title: 'Target Amount',
            key: 'target_amount',
            align: 'right',
            width: 140,
            render: (_, tx) => {
                const rawAmount = tx.targetAmount
                if (rawAmount === null || rawAmount === undefined) return <span className="text-gray-400">—</span>
                return (
                    <div className="whitespace-nowrap flex items-center justify-end gap-1.5">
                        <span className="text-sm text-gray-900 tabular-nums">
                            {formatAmount(rawAmount, getMaxDecimalsForCurrency(tx.toCurrency))}
                        </span>
                    </div>
                )
            },
        },
        {
            title: 'Requested Rate',
            key: 'target_rate',
            align: 'right',
            width: 130,
            render: (_, tx) => {
                const rawAmount = tx.targetRate
                if (rawAmount === null || rawAmount === undefined) return <span className="text-gray-400">—</span>
                return (
                    <div className="whitespace-nowrap flex items-center justify-end gap-1.5">
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                            {formatAmount(rawAmount, getMaxDecimalsForCurrency(tx.fromCurrency))}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-md whitespace-nowrap">
                            {tx.fromCurrency}
                        </span>
                    </div>
                )
            },
        },
        {
            title: 'OTC Final Rate',
            key: 'otc_final_rate',
            align: 'right',
            width: 130,
            render: (_, tx) => {
                const rawAmount = tx.otcFinalRate
                if (rawAmount === null || rawAmount === undefined) return <span className="text-gray-400">—</span>
                return (
                    <div className="whitespace-nowrap flex items-center justify-end gap-1.5">
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                            {formatAmount(rawAmount, getMaxDecimalsForCurrency(tx.fromCurrency))}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-md whitespace-nowrap">
                            {tx.fromCurrency}
                        </span>
                    </div>
                )
            },
        },
        {
            title: 'Created At',
            key: 'created_at',
            width: 140,
            render: (_, tx) => {
                const dateFormatted = fmtDate(tx.createdAt)
                if (!dateFormatted) return <span className="text-gray-400">—</span>
                return (
                    <div className="leading-tight whitespace-nowrap">
                        <p className="text-xs font-medium text-gray-800 m-0">{dateFormatted}</p>
                    </div>
                )
            },
        },
        {
            title: 'Accepted At',
            key: 'accepted_at',
            width: 140,
            render: (_, tx) => {
                const dateFormatted = fmtDate(tx.acceptedAt)
                if (!dateFormatted) return <span className="text-gray-400">—</span>
                return (
                    <div className="leading-tight whitespace-nowrap">
                        <p className="text-xs font-medium text-gray-800 m-0">{dateFormatted}</p>
                    </div>
                )
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 140,
            render: (_, tx) => {
                const status = tx.status;
                return (
                    <div className="whitespace-nowrap">
                        <Badge status={status || 'INACTIVE'} />
                    </div>
                )
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            align: 'right',
            render: (record) => {
                const isViewOnly = ['ACCEPTED', 'REJECTED', 'EXPIRED', 'COMPLETED'].includes(record.status);
                return (
                    <button
                        type="button"
                        onClick={() => {
                            navigate(`/sender/otc-requests/${record.otcRequestId}`, {
                                state: { paymentRequestId: record.paymentRequestId },
                            })
                        }}
                        className="inline-flex items-center justify-center gap-1.5 p-1.5 text-sm rounded-lg text-blue-600 hover:text-blue-800 transition-colors cursor-pointer border-0 bg-transparent"
                    >
                        {isViewOnly ? 'View' : <>Manage  <ArrowRightOutlined /></>}
                    </button>
                )
            },
        },
    ]


    return (
        <AppShell>
            <div>
                {error && (
                    <Alert type="error" message={error} showIcon className="mb-4" />
                )}
                <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-xs">
                    <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                            <div>
                                <h2 className="text-sm font-semibold text-gray-800 m-0">OTC Requests</h2>
                                <p className="text-xs font-medium text-gray-500 m-0">Manage Large Volume Negotiated Trades</p>
                            </div>
                            {otcData?.meta?.total > 0 && (
                                <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full border border-gray-200">
                                    {otcData.meta.total.toLocaleString()} total
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Select
                                value={statusFilter || undefined}
                                placeholder="All Statuses"
                                allowClear
                                showSearch={false}
                                onChange={(val) => {
                                    setStatusFilter(val || '')
                                    setPage(1)
                                }}
                                className="min-w-[150px]"
                                options={[
                                    { value: '', label: 'All Statuses' },
                                    ...Object.values(OTC_STATUS_OPTIONS).map((s) => ({
                                        value: s,
                                        label: <span className="capitalize">{s}</span>,
                                    })),
                                ]}
                            />
                            {!DISABLE_DATE_FILTER && (
                                <DateFilter onChange={changeDateFilter} />
                            )}
                        </div>
                    </div>
                    <Table
                        className="admin-table table-blue-zebra"
                        columns={otcColumnStructure}
                        dataSource={otcData.otcRequests}
                        rowKey={(r) => r.otcRequestId}
                        loading={loadingTxns}
                        locale={{
                            emptyText: loadingTxns ? (
                                <div className="py-12" />
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        statusFilter
                                            ? `No ${statusFilter} requests found.`
                                            : 'No OTC requests found.'
                                    }
                                />
                            ),
                        }}
                        pagination={{
                            current: page,
                            pageSize,
                            total,
                            showSizeChanger: { showSearch: false },
                            showQuickJumper: false,
                            pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS.map(String),
                            responsive: true,
                            onShowSizeChange: (_current, newSize) => {
                                setPageSize(newSize)
                                setPage(1)
                            },
                            onChange: (newPage, newPageSize) => {
                                setPage(newPage)
                                if (newPageSize && newPageSize !== pageSize) {
                                    setPageSize(newPageSize)
                                    setPage(1)
                                }
                            },
                            showTotal: (totalCount, range) => (
                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                    Showing{' '}
                                    <span className="font-medium text-gray-800">
                                        {range[0]}-{range[1]}
                                    </span>{' '}
                                    of{' '}
                                    <span className="font-medium text-gray-800">
                                        {totalCount}
                                    </span>{' '}
                                    requests
                                </span>
                            ),
                        }}
                    />
                </div>
            </div>

        </AppShell>
    )
}

export default OtcList
